import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {MatSnackBar} from '@angular/material/snack-bar';
import {finalize, take} from 'rxjs/operators';
import {FileDTO} from '../../../clm/models/FileDTO';
import {FileUploadService} from '../../services/file-upload.service';
import {AngularFileUploaderConfig, ReplaceTexts} from './file-uploader.types';
import {KeycloakService} from 'keycloak-angular';
import {generateRandomString} from '../../util/Util';

@Component({
  selector: 'app-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.css']
})
export class FileUploadComponent implements OnInit, OnChanges {

  @Input() config: AngularFileUploaderConfig;
  @Input() resetUpload = false;
  @Input() customCssClasses: Array<string> = [];
  @Input() showCustomText: boolean;
  @Input() customText: string;
  @Input() showIcon: boolean;
  @Input() iconName: string;
  @Input() disabled: boolean = false;
  @Input() randomStringLength = 5;
  @Input() fileNameOverride: string;

  @Output() showBusyCounter = new EventEmitter();
  @Output() fileSelected: EventEmitter<any> = new EventEmitter<any>();
  @Output() onUploadSuccess: EventEmitter<any> = new EventEmitter<any>();

  id: string;
  configName: string;
  isUploading: boolean;
  isEditMode = false;
  maxSize: number;
  multiple: boolean;
  fileToUpload: File;
  formatsAllowed: string;
  fileExtension: string;
  fileUploadType: string;
  fileNameWithoutExtension: string;
  fileDescription: string;
  fileDTO: FileDTO;
  allowedFiles: File[] = [];
  replaceTexts: ReplaceTexts;

  readonly DEFAULT_UPLOAD_FILE_SIZE = 10; // 10 * (1 * 102400) -> bytes filesize is allowed

  private loggedInUserName: string;

  constructor(private fileUploadService: FileUploadService,
              private httpClient: HttpClient,
              private snackBar: MatSnackBar,
              private keycloakService: KeycloakService) {
    this.fileToUpload = null;
  }

  ngOnInit(): void {
    this.keycloakService
      .loadUserProfile()
      .then(profile => {
        this.loggedInUserName = `${profile.firstName} ${profile.lastName}`;
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    // Track changes in Configuration and see if user has even provided Configuration.
    if (changes.config && this.config) {
      // Assign User Configurations to Library Properties.
      this.id = this.config.id || generateRandomString(this.randomStringLength);
      this.configName = this.config.name || 'default';
      this.maxSize = (this.config.maxSize || this.DEFAULT_UPLOAD_FILE_SIZE) * 1024000;
      this.formatsAllowed = this.config.formatsAllowed || '*';
      this.formatsAllowed === '*' ? '' : '(' + this.formatsAllowed + ')';
      this.multiple = this.config.multiple || false;
      this.replaceTexts = {
        selectFileBtn: this.multiple ? 'UPLOADS' : 'UPLOAD',
        uploadBtn: 'UPLOAD FILE',
        afterUploadMsg_success: 'Successfully Uploaded!',
        afterUploadMsg_error: 'Upload Failed!',
        sizeLimit: 'Size Limit'
      }; // default replaceText.
      this.fileDescription = this.config.description || '';
      if (this.config.replaceTexts) {
        // updated replaceText if user has provided any.
        this.replaceTexts = {
          ...this.replaceTexts,
          ...this.config.replaceTexts
        };
      }

      if (this.config.isEditMode) {
        this.fileDTO = this.config.file;
        this.isEditMode = true;
      }
    }

    // Reset when resetUpload value changes from false to true.
    if (changes.resetUpload) {
      if (changes.resetUpload.currentValue === true) {
        this.resetFileUpload();
      }
    }
  }

  handleFileInput($event) {
    const files = $event.target.files;
    if (files.length === 0) {
      this.openSnackBar('No file chosen', 'Dismiss');
      return;
    }

    this.fileToUpload = files.item(0);
    const isFileSizeWithinMaxFileSizeLimit = this.checkIsValidFileSize(this.fileToUpload);

    if (isFileSizeWithinMaxFileSizeLimit) {
      const indexBeforeExtension = this.fileToUpload.name.lastIndexOf('.');
      this.fileNameWithoutExtension = this.fileNameOverride ? this.fileNameOverride : this.fileToUpload.name.slice(0, indexBeforeExtension);
      this.fileExtension = '.' + this.fileToUpload.name.slice(indexBeforeExtension + 1);

      this.fileDescription = this.fileNameOverride ? this.fileToUpload.name : this.fileDescription;

      // Emitted Selected file:
      const fileSelectedData = {
        ...this.fileToUpload,
        configName: this.configName,
        name: this.fileNameWithoutExtension,
        description: this.fileDescription,
        extension: this.fileExtension,
        date: new Date()
      };
      this.fileSelected.emit(fileSelectedData);
      this.onUploadFile($event);
    }
    else {
      this.resetFileUpload();
    }
  }

  handleFileInputReset = (event) => {
    event.target.value = '';
  }

  async onUploadFile($event) {
    $event.stopPropagation(); // Prevent to open file dialog
    const base64Data = await this.readFileAsDataURL(this.fileToUpload);
    const fileObj = this.getFileDTOPayload(base64Data);
    this.isUploading = true;
    this.showBusyCounter.emit(this.isUploading);
    this.fileUploadService.uploadFile(fileObj)
      .pipe(
        take(1),
        finalize(() => {
          this.isUploading = false;
          this.showBusyCounter.emit(this.isUploading);
        })
      )
      .subscribe((response: FileDTO) => {
        if (response) {
          this.openSnackBar(this.replaceTexts.afterUploadMsg_success, 'Dismiss');
          this.onUploadSuccess.emit({
            ...response,
            date: new Date(),
            configName: this.configName,
            type: this.fileUploadType
          });
          this.resetFileUpload();
        }
        else {
          this.openSnackBar(this.replaceTexts.afterUploadMsg_error, 'Dismiss');
        }
      }, (error) => {
        this.openSnackBar(this.replaceTexts.afterUploadMsg_error, 'Dismiss');
        console.log(error);
      });
  }

  readFileAsDataURL(file) {
    return new Promise((resolve, reject) => {
      try {
        const reader = new FileReader();
        reader.onload = (event: any) => {
          this.fileUploadType = event.target.result.split(',')[0];
          const base64String = event.target.result.split(',').pop();
          resolve(base64String);
        };
        reader.readAsDataURL(file);
      } catch (error) {
        console.log(error);
        reject(error);
      }
    });
  }

  openFileExplorer($event) {
    $event.stopPropagation();
    const element = document.getElementById('sel' + this.id);
    if (element !== null) {
      element.click();
    }
    else {
      this.openSnackBar(`Unable to open File explorer`, 'Dismiss');
    }
  }

  private getFileDTOPayload(base64Data) {
    const fileObj: FileDTO = {
      created: new Date(),
      createdBy: this.loggedInUserName,
      tenantId: null,
      updated: null,
      updatedBy: null,
      fileId: this.isEditMode ? null : null,
      name: this.isEditMode ? null : this.fileNameWithoutExtension,
      file: base64Data,
      extension: this.isEditMode ? null : this.fileExtension,

      // Setting of description when provided on initial config || config reference updated
      description: this.fileDescription || this.config.description
    };
    return fileObj;
  }

  // Reset following properties.
  private resetFileUpload() {
    this.allowedFiles = [];
    this.fileToUpload = null;
    this.fileExtension = null;
    this.fileNameWithoutExtension = null;
  }

  private checkIsValidFileSize(file) {
    if (file.size <= this.maxSize) {
      return true;
    }
    else {
      const selectedFileSize = this.bytesToMegaBytes(file.size);
      const maxFileSizeAllowed = this.bytesToMegaBytes(this.maxSize);
      this.openSnackBar(`Please select file smaller than ${maxFileSizeAllowed} mb`, 'Dismiss');
      return false;
    }
  }

  private bytesToMegaBytes(bytes) {
    return Math.round(bytes / (1024 * 1024));
  }

  private openSnackBar(message: string, action: string) {
    this.snackBar.open(message, action, {
      duration: 10000
    });
  }

}
